/**
 * DEPRECATION NOTICE
 * This component is being kept for backward compatibility, but new implementations
 * should prefer using `FileUpload.tsx` which is more versatile and flexible:
 * - Works for images and generic files
 * - Built-in cropping (aspect ratio, shape) and resizing controls
 * - Cleaner drag-and-drop UX and multiple file support
 * - Centralized validation and error handling
 *
 * Please migrate usages of `ImageUpload` to `FileUpload` when convenient.
 */
import { useRef, useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import Croppie from 'croppie';
import 'croppie/croppie.css';
import clsx from 'clsx';
import { PhotoIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Modal } from '../ui/Modal';
import { Button } from './Button';
import { 
  processImage, 
  getCroppieResult, 
  initCroppie, 
  CroppieConfig
} from '../../../utils/imageUtils';
import { uploadFile, FileReference } from '../../../utils/filestorageUtils';
import { supabase } from '../../../lib/supabase';
import { captureAppException } from '../../../utils/errorCaptureUtils';

// Configuration for different image types
const IMAGE_CONFIGS = {
  header: {
    targetWidth: 1200,
    targetHeight: 400,
    aspectRatio: 3,
    type: 'square' as const,
    quality: 0.74,
    prefix: 'header',
    containerClass: 'aspect-[3/1]',
    previewClass: 'rounded-md',
    label: 'Header Image',
    recommendedSize: '1200x400px'
  },
  logo: {
    targetWidth: 320,
    targetHeight: 320,
    aspectRatio: 1,
    type: 'circle' as const,
    quality: 0.85,
    prefix: 'logo',
    containerClass: 'aspect-square w-40 h-40',
    previewClass: 'rounded-full',
    label: 'Logo',
    recommendedSize: '320x320px'
  },
  avatar: {
    targetWidth: 320,
    targetHeight: 320,
    aspectRatio: 1,
    type: 'circle' as const,
    quality: 0.85,
    prefix: 'avatar',
    containerClass: 'aspect-square w-32 h-32',
    previewClass: 'rounded-full',
    label: 'Avatar',
    recommendedSize: '320x320px'
  },
  post: {
    targetWidth: 1024,
    targetHeight: 576,
    aspectRatio: 16/9,
    type: 'square' as const,
    quality: 0.80,
    prefix: 'post',
    containerClass: 'aspect-[16/9] w-full max-w-md',
    previewClass: 'rounded-lg',
    label: 'Post Image',
    recommendedSize: '1024x576px'
  }
} as const;

type ImageType = keyof typeof IMAGE_CONFIGS;

export interface ImageUploadRef {
  uploadPendingFile: (postId: string, postTimestamp: string) => Promise<void>;
}

interface ImageUploadProps {
  type: ImageType;
  currentImageUrl?: string | null;
  onImageChange?: (path: string | null) => Promise<void>;
  maxSizeMB?: number;
  bucketName: string;
  filePath: string;
  tableName: string;
  columnName: string;
  recordId: string;
  className?: string;
  label?: string;
  helperText?: string;
  error?: boolean | string;
  disabled?: boolean;
  onDelete?: () => void;
  delayUpload?: boolean;
}

export const ImageUpload = forwardRef<ImageUploadRef, ImageUploadProps>(({
  type,
  currentImageUrl,
  onImageChange,
  maxSizeMB = 10,
  bucketName,
  filePath,
  tableName,
  columnName,
  recordId,
  label,
  helperText,
  error,
  disabled = false,
  onDelete,
  delayUpload = false,
}, ref) => {
  const config = IMAGE_CONFIGS[type];
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const croppieContainerRef = useRef<HTMLDivElement | null>(null);
  const croppieInstanceRef = useRef<Croppie | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  useImperativeHandle(ref, () => ({
    uploadPendingFile: async (postId: string, postTimestamp: string) => {
      if (!pendingFile) return;
      const finalFilePath = filePath.replace('new', postId).replace(`${config.prefix}.png`, `${config.prefix}_${postTimestamp}.png`);
      
      try {
        setLoading(true);
        setValidationError(null);
        
        const { error: uploadError } = await supabase.storage
          .from(bucketName)
          .upload(finalFilePath, pendingFile, {
            cacheControl: '3600',
            upsert: true
          });

        if (uploadError) throw uploadError;

        const { error: updateError } = await supabase
          .from(tableName)
          .update({ [columnName]: finalFilePath })
          .eq('id', postId);

        if (updateError) throw updateError;

        setPendingFile(null);
      } catch (err) {
        captureAppException(err, {
          tags: { context: 'image_upload', type: config.type },
          extra: { 
            postId,
            postTimestamp,
            filePath: finalFilePath,
            bucketName,
            tableName,
            columnName
          }
        });
        setValidationError('Failed to upload image');
        throw err;
      } finally {
        setLoading(false);
      }
    }
  }));

  useEffect(() => {
    return () => {
      croppieInstanceRef.current?.destroy();
    };
  }, []);

  const initCroppieInstance = () => {
    if (!croppieContainerRef.current) return;

    const containerWidth = croppieContainerRef.current.offsetWidth;
    const croppieConfig: CroppieConfig = {
      containerWidth,
      aspectRatio: config.aspectRatio,
      type: config.type,
      enableZoom: true,
      showZoomer: true
    };

    croppieInstanceRef.current = initCroppie(
      croppieContainerRef.current,
      croppieConfig,
      croppieInstanceRef.current
    );
  };

  useEffect(() => {
    if (!showModal || !selectedImage || !croppieContainerRef.current) return;
    initCroppieInstance();
    croppieInstanceRef.current?.bind({ url: selectedImage });
  }, [showModal, selectedImage]);

  useEffect(() => {
    function handleResize() {
      if (showModal) {
        initCroppieInstance();
        croppieInstanceRef.current?.bind({ url: selectedImage! });
      }
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [showModal, selectedImage]);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const processed = await processImage(file, {
        maxSizeMB,
        allowedTypes: ['image/png', 'image/jpeg', 'image/gif', 'image/webp'],
        processing: {
          maxWidth: config.targetWidth * 2,
          maxHeight: config.targetHeight * 2,
          quality: 1,
          format: 'png'
        }
      });

      setSelectedImage(processed.objectUrl);
      setShowModal(true);
      setValidationError(null);
    } catch (err) {
      captureAppException(err, {
        tags: { context: 'image_upload', type: config.type, action: 'file_select' },
        extra: { 
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
          maxSizeMB
        }
      });
      setValidationError('Invalid image file');
    }

    if (event.target) {
      event.target.value = '';
    }
  };

  const handleClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleInternalSave = async () => {
    if (!croppieInstanceRef.current) return;
    setLoading(true);
    setValidationError(null);

    try {
      const { blob } = await getCroppieResult(croppieInstanceRef.current, {
        type: 'blob',
        format: 'png',
        quality: 1,
        circle: config.type === 'circle'
      });

      const processed = await processImage(new File([blob], `${config.prefix}.png`, { type: 'image/png' }), {
        maxSizeMB,
        allowedTypes: ['image/png'],
        processing: {
          maxWidth: config.targetWidth,
          maxHeight: config.targetHeight,
          quality: config.quality,
          format: 'png'
        }
      });

      if (delayUpload) {
        setPendingFile(processed.file);
        setSelectedImage(processed.objectUrl);
        if (onImageChange) await onImageChange('pending');
        setShowModal(false);
        return;
      }

      // Generate unique filename with timestamp
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const uniqueFilename = `${config.prefix}_${timestamp}.png`;
      const uniqueFilePath = filePath.replace(`${config.prefix}.png`, uniqueFilename);

      const file = new File([processed.blob], uniqueFilename, { type: 'image/png' });
      const fileRef: FileReference = { bucket: bucketName, path: uniqueFilePath };

      await uploadFile(
        fileRef,
        file,
        async () => {
          const { error: updateError } = await supabase
            .from(tableName)
            .update({ [columnName]: uniqueFilePath })
            .eq('id', recordId);

          if (updateError) throw updateError;
          if (onImageChange) await onImageChange(uniqueFilePath);
        },
        (error) => {
          throw error;
        }
      );

      setShowModal(false);
      setSelectedImage(null);
    } catch (err) {
      captureAppException(err, {
        tags: { context: 'image_upload', type: config.type, action: 'save' },
        extra: { 
          recordId,
          filePath,
          bucketName,
          tableName,
          columnName
        }
      });
      setValidationError('Could not save image');
    } finally {
      setLoading(false);
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedImage(null);
    croppieInstanceRef.current?.destroy();
    croppieInstanceRef.current = null;
  };

  const handleRemoveClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!disabled) {
      setShowDeleteModal(true);
    }
  };

  const handleConfirmDelete = async () => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from(tableName)
        .update({ [columnName]: '' })
        .eq('id', recordId);

      if (error) throw error;
      if (onImageChange) await onImageChange('');
      if (onDelete) await onDelete();
      setShowDeleteModal(false);
    } catch (err) {
      captureAppException(err, {
        tags: { context: 'image_upload', type: config.type, action: 'delete' },
        extra: { 
          recordId,
          tableName,
          columnName
        }
      });
      setValidationError('Failed to remove image');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={clsx('space-y-1.5 w-full', config.containerClass)}>
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
        </label>
      )}
      <div
        className={clsx(
          'group relative mt-1 flex justify-center items-center px-6 pt-5 pb-6 border-2 border-dashed transition-colors',
          config.containerClass,
          disabled ? 'bg-gray-100 cursor-not-allowed' : 'border-gray-300 hover:border-primary-500 cursor-pointer bg-white',
          error ? 'border-red-500' : '',
          (currentImageUrl || selectedImage) ? 'border-solid' : 'border-dashed'
        )}
        onClick={!currentImageUrl && !selectedImage ? handleClick : undefined}
        onKeyDown={(e) => e.key === 'Enter' && !disabled && !currentImageUrl && !selectedImage && handleClick()}
        role="button"
        tabIndex={disabled ? -1 : 0}
      >
        {(currentImageUrl || selectedImage) ? (
          <>
            <img
              src={currentImageUrl || selectedImage || ''}
              alt={`${config.label} preview`}
              className={clsx('object-cover w-full h-full', config.previewClass)}
            />
            {!disabled && (
              <>
                <button
                  type="button"
                  onClick={handleRemoveClick}
                  disabled={loading}
                  className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 focus:opacity-100 transition-opacity z-10"
                  aria-label={`Remove ${config.label.toLowerCase()}`}
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={handleClick}
                  disabled={loading}
                  className="absolute inset-0 w-full h-full flex items-center justify-center bg-black/50 text-white opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 focus:opacity-100 transition-opacity z-[5]"
                >
                  <span className="text-sm font-medium">Change {config.label.toLowerCase()}</span>
                </button>
              </>
            )}
          </>
        ) : (
          <div className="space-y-1 text-center">
            <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
            <div className="flex text-sm text-gray-600">
              <span className={clsx(!disabled && "text-primary-600 group-hover:text-primary-500", "font-medium")}>
                {disabled ? config.label : `Upload a ${config.label.toLowerCase()}`}
              </span>
            </div>
            <p className="text-xs text-gray-500">
              PNG, JPG, GIF, WEBP up to {maxSizeMB}MB. Recommended: {config.recommendedSize}.
            </p>
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
          disabled={disabled || loading}
        />
      </div>
      {helperText && !error && (
        <p className="mt-2 text-sm text-gray-500">{helperText}</p>
      )}
      {validationError && (
        <p className="mt-2 text-sm text-red-600">{validationError}</p>
      )}
      {typeof error === 'string' && (
        <p className="mt-2 text-sm text-red-600">{error}</p>
      )}

      {showModal && selectedImage && (
        <Modal
          isOpen={showModal}
          onClose={handleModalClose}
          size={type === 'header' ? '3xl' : 'lg'}
          closeOnEsc={false}
          closeOnClickOutside={false}
        >
          <div className="p-4">
            <h3 className="text-xl font-semibold mb-4 text-gray-800">Crop {config.label}</h3>
            <div
              ref={croppieContainerRef}
              className="w-full h-[60vh]"
              aria-label="Image cropper"
            />
            {validationError && (
              <p className="mt-2 text-sm text-red-600">{validationError}</p>
            )}
            <div className="mt-6 flex justify-end space-x-3">
              <Button variant="outline" onClick={handleModalClose} disabled={loading}>
                Cancel
              </Button>
              <Button color="primary" onClick={handleInternalSave} loading={loading} disabled={loading}>
                Save {config.label}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {showDeleteModal && (
        <Modal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          size="sm"
        >
          <div className="p-4">
            <h3 className="text-lg font-semibold mb-2 text-gray-800">Remove {config.label}</h3>
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to remove this {config.label.toLowerCase()}? This action cannot be undone.
            </p>
            {validationError && (
              <p className="mt-2 text-sm text-red-600">{validationError}</p>
            )}
            <div className="mt-6 flex justify-end space-x-3">
              <Button variant="outline" onClick={() => setShowDeleteModal(false)} disabled={loading}>
                Cancel
              </Button>
              <Button color="danger" onClick={handleConfirmDelete} loading={loading} disabled={loading}>
                Remove {config.label}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}); 